// @flow

import { useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import ViewTasksLink from '@kiosk/common/components/UserPanel/ViewTasksLink/ViewTasksLink';

import type {
  Shift,
  User,
  SignOutActionCreator,
  StartBreakActionCreator,
  EndBreakActionCreator,
  ClockOutActionCreator
} from '../../typings';
import AnimatedRow from '../Forms/AnimatedRow';
import RowIcon from '../Forms/RowIcon';
import RowContent from '../Forms/RowContent';
import Label from '../Text/Label';
import ContentText from '../Text/ContentText';
import ButtonContainer from '../Forms/ButtonContainer';
import FormContainer from '../Forms/FormContainer';
import FormScreenContainer from '../ui/FormScreenContainer';
import PaddedContainer from '../ui/PaddedContainer';
import Button from '../Button';
import getTotalDurationMinutes from '../../../timeClock/utils/getTotalDurationMinutes';
import { getDisplayDuration } from '../../../utils/Utils';
import { SMALL_DEVICE_HEIGHT } from '../../../constants';
import useTaskViewRenderers from '../../../shiftTasks/helpers/useTaskViewRenderers';
import infoGrayIcon from '../../../assets/svg/icons/info-gray.svg';
import TimeClockNote from '../../../timeClock/components/TimeClockNote';

import TimesheetEntries from './TimesheetEntries';

const FormFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-height: ${SMALL_DEVICE_HEIGHT}px) {
    overflow: visible;
  }

  @media (min-height: ${SMALL_DEVICE_HEIGHT + 1}px) {
    margin-top: 40px;
    max-height: ${props => props.deviceHeight * 0.5}px;
    overflow-x: hidden;
  }
`;

type Props = {
  usTimeFormat: boolean,
  user: User | null,
  shift: Shift,
  signOut: SignOutActionCreator,
  startBreak: StartBreakActionCreator,
  endBreak: EndBreakActionCreator,
  clockOut: ClockOutActionCreator,
  shiftBreakType: boolean,
  deviceHeight: number,
  isIntegrationKiosk: boolean
};

export default function ClockedInFields({
  usTimeFormat,
  user,
  shift,
  signOut,
  startBreak,
  endBreak,
  clockOut,
  shiftBreakType,
  deviceHeight,
  isIntegrationKiosk
}: Props) {
  const ref = useRef();
  const noteRef = useRef();
  const { viewTasks, shortenAnimations, render, maxHeight, setMaxHeight } = useTaskViewRenderers({
    shift,
    user,
    deviceHeight,
    renderProp: renderClockInFields,
    baseScreenRef: ref
  });

  const saveNote = () => {
    noteRef.current?.saveNote();
  };

  const buttons = generateButtons({
    shift,
    signOut,
    clockOut,
    startBreak,
    endBreak,
    shiftBreakType,
    shortenAnimations,
    isIntegrationKiosk,
    saveNote
  });
  const totalDuration = getTotalDurationMinutes(shift.timesheetProjections);
  const hasTasks = Boolean(shift.taskInstances?.length);

  useLayoutEffect(() => {
    if (ref.current) {
      const { height: currentScreenHeight } = ref.current.getBoundingClientRect();
      if (currentScreenHeight > maxHeight) {
        setMaxHeight(currentScreenHeight);
      }
    }
  });

  function renderClockInFields() {
    const delay = shortenAnimations ? 0.2 : 1.2;
    return (
      <div ref={ref}>
        {hasTasks && <ViewTasksLink delay={delay} onClick={viewTasks} />}

        <FormScreenContainer>
          <FormContainer>
            <FormFieldsContainer deviceHeight={deviceHeight}>
              <PaddedContainer>
                <TimesheetEntries
                  timesheetProjections={shift.timesheetProjections}
                  usTimeFormat={usTimeFormat}
                  initialAnimationDelay={delay}
                />
                {!!totalDuration && (
                  <AnimatedRow borderBottom delay={delay}>
                    <RowIcon src={infoGrayIcon} />
                    <RowContent>
                      <Label>Duration</Label>
                      <ContentText>{getDisplayDuration(totalDuration)}</ContentText>
                    </RowContent>
                  </AnimatedRow>
                )}

                <TimeClockNote delay={delay} ref={noteRef} shift={shift} />
                {deviceHeight <= SMALL_DEVICE_HEIGHT && buttons}
              </PaddedContainer>
            </FormFieldsContainer>
            {deviceHeight > SMALL_DEVICE_HEIGHT && buttons}
          </FormContainer>
        </FormScreenContainer>
      </div>
    );
  }

  return render();
}

ClockedInFields.propTypes = {
  usTimeFormat: PropTypes.bool,
  user: PropTypes.object,
  shift: PropTypes.object,
  signOut: PropTypes.func,
  startBreak: PropTypes.func,
  endBreak: PropTypes.func,
  clockOut: PropTypes.func,
  shiftBreakType: PropTypes.bool,
  deviceHeight: PropTypes.number,
  isIntegrationKiosk: PropTypes.bool
};

function generateButtons({
  shift,
  signOut,
  clockOut,
  startBreak,
  endBreak,
  shiftBreakType,
  shortenAnimations,
  isIntegrationKiosk,
  saveNote
}) {
  if (shift.clockInStatus.onBreak) {
    return generateButtonsForOnBreakState({
      signOut,
      clockOut,
      endBreak,
      shiftBreakType,
      shortenAnimations,
      isIntegrationKiosk,
      saveNote
    });
  }
  return generateButtonsForClockInState({
    signOut,
    clockOut,
    startBreak,
    shiftBreakType,
    shortenAnimations,
    isIntegrationKiosk,
    saveNote
  });
}

type ClockInProps = {
  signOut: SignOutActionCreator,
  clockOut: ClockOutActionCreator,
  startBreak: StartBreakActionCreator,
  shiftBreakType: boolean,
  shortenAnimations: boolean,
  isIntegrationKiosk: boolean,
  saveNote: () => void
};
function generateButtonsForClockInState({
  signOut,
  clockOut,
  startBreak,
  shiftBreakType,
  shortenAnimations,
  isIntegrationKiosk,
  saveNote
}: ClockInProps) {
  let delay = shortenAnimations ? 0 : 1;
  delay = delay + (shiftBreakType ? 0.4 : 0.2);
  return (
    <ButtonContainer delay={delay}>
      {shiftBreakType && (
        <Button
          label="Start break"
          buttonTheme="grey"
          themeColor="btnBreakTextColor"
          onPress={() => {
            startBreak();
            saveNote();
          }}
        />
      )}
      <Button
        label="Clock out"
        buttonTheme="grey"
        themeColor="errorTextColor"
        onPress={() => {
          clockOut();
          saveNote();
        }}
        withMargin
      />
      {!isIntegrationKiosk && (
        <Button label="Cancel" buttonTheme="grey" onPress={signOut} withMargin />
      )}
    </ButtonContainer>
  );
}

type OnBreakProps = {
  signOut: SignOutActionCreator,
  clockOut: ClockOutActionCreator,
  endBreak: EndBreakActionCreator,
  shiftBreakType: boolean,
  shortenAnimations: boolean,
  isIntegrationKiosk: boolean,
  saveNote: () => void
};
function generateButtonsForOnBreakState({
  signOut,
  clockOut,
  endBreak,
  shiftBreakType,
  shortenAnimations,
  isIntegrationKiosk,
  saveNote
}: OnBreakProps) {
  let delay = shortenAnimations ? 0 : 1;
  delay = delay + (shiftBreakType ? 0.4 : 0.2);
  return (
    <ButtonContainer delay={delay}>
      {shiftBreakType && (
        <Button
          label="End break"
          buttonTheme="grey"
          themeColor="btnBreakTextColor"
          onPress={() => {
            endBreak();
            saveNote();
          }}
        />
      )}
      <Button
        label="Clock out"
        buttonTheme="grey"
        themeColor="errorTextColor"
        onPress={() => {
          clockOut();
          saveNote();
        }}
        withMargin
      />
      {!isIntegrationKiosk && (
        <Button label="Cancel" buttonTheme="grey" onPress={signOut} withMargin />
      )}
    </ButtonContainer>
  );
}
